import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { compose } from 'utils/react';
import { section } from '../decorators';
import { editor } from '../../widgets/decorators';
import useCurrentSite from '../../../../../modules/react/hooks/useCurrentSite';
import Editor from './Editor';
import { PARTIAL_OVERLAY, FULL_OVERLAY } from './HeroStylesInput';
import styles from './SimpleHeroSlideShow.scss';

const propTypes = {
  children: PropTypes.node.isRequired,
  heroImages: PropTypes.arrayOf(PropTypes.shape({
    file: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
  })),
  displayStyle: PropTypes.string,
  isEditable: PropTypes.bool,
};

const SimpleHeroSlideShow = ({
  children,
  heroImages: images,
  displayStyle,
  isEditable,
}) => {
  const { id: companyId } = useCurrentSite();
  const isEditingAChild = isEditable && React.Children.map(children, child =>
    get(child, 'props.isEditing', false)).some(isEditing => isEditing);
  const [title, bragging, button, Carousel] = React.Children.toArray(children);
  const heroImages = images.filter(image => (image && image.file) || (image && image.mediumId)).map((image) => {
    if (image.mediumId) {
      return image;
    }

    const { file } = image;
    return ({
      ...image,
      filename: file.replace(`${companyId}/`, ''),
    });
  });

  const classNames = [styles.root];
  if (displayStyle === FULL_OVERLAY) {
    classNames.push(styles.fullOverlay);
  } else if (displayStyle === PARTIAL_OVERLAY) {
    classNames.push(styles.partialOverlay);
  }

  if (heroImages.length > 1) {
    classNames.push(styles.hasControls);
  }

  return (
    <div className={classNames.join(' ')}>
      <div className={styles.content}>
        <div className={styles.contentOverlay}>
          <div className={styles.jumboHeader}>
            {title}
          </div>
          <div className={styles.bragging}>
            {bragging}
          </div>
          <div className={styles.buttonContainer}>
            {button}
          </div>
        </div>
      </div>
      {React.cloneElement(Carousel, { heroImages, isEditingAChild })}
    </div>
  );
};

SimpleHeroSlideShow.propTypes = propTypes;

export default compose(
  editor(Editor),
  section(
    {
      name: 'Hero: Slideshow',
      category: 'Images',
      defaultPayload: {
        heroImages: [{
          alt: 'Hero Image',
          file: '/img/hero.jpg',
          link: '',
          target: '',
          title: '',
        }],
        displayStyle: 'FULL_OVERLAY',
      },
    },
    {
      subsections: [
        { accepts: ['HeaderText'] },
        { accepts: ['ContentBox'] },
        { accepts: ['Button'] },
        { accepts: ['Carousel'] },
      ],
      defaults: [
        ['HeaderText', {
          rawContentState: {
            blocks: [
              {
                key: 'db2mm',
                text: 'Awesome Self Storage',
                type: 'header-one',
                depth: 0,
                inlineStyleRanges: [
                  {
                    offset: 0,
                    length: 20,
                    style: 'COLOR:#ffffff',
                  },
                ],
                entityRanges: [],
              },
            ],
            entityMap: {},
          },
        }],
        ['ContentBox', {
          rawContentState: {
            blocks: [
              {
                key: 'db2mm',
                text: 'This is where you brag about how fantastic your place is',
                type: 'unstyled',
                depth: 0,
                inlineStyleRanges: [
                  {
                    offset: 0,
                    length: 56,
                    style: 'COLOR:#ffffff',
                  },
                ],
                entityRanges: [],
              },
            ],
            entityMap: {},
          },
        }],
        ['Button', { text: 'Start Storing Today' }],
        ['Carousel'],
      ],
      menu: { items: ['edit', 'delete'] },
    },
  ),
)(SimpleHeroSlideShow);
